/**
 * Studio AI Welcome Modal
 * Welcomes users and informs them about infrastructure and tips
 */

import { useTranslation } from "react-i18next"

import { Globe2, Info, Lightbulb, Sparkles } from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

type StudioAIWelcomeModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function StudioAIWelcomeModal({ open, onOpenChange }: StudioAIWelcomeModalProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi.welcomeModal" })

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-2xl">
        <AlertDialogHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="text-primary h-6 w-6" />
            <AlertDialogTitle className="text-2xl">{t("title")}</AlertDialogTitle>
          </div>
        </AlertDialogHeader>
        <AlertDialogBody>
          <div className="space-y-4 text-left">
            <p className="text-base leading-relaxed">{t("intro")}</p>

            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="text-primary flex-shrink-0 pt-1">
                  <Globe2 aria-hidden="true" className="size-5" />
                </div>
                <div>
                  <h4 className="text-foreground font-semibold">{t("infraTitle")}</h4>
                  <p className="text-muted-foreground text-sm leading-relaxed">{t("infraDesc")}</p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="text-primary flex-shrink-0 pt-1">
                  <Lightbulb aria-hidden="true" className="size-5" />
                </div>
                <div>
                  <h4 className="text-foreground font-semibold">{t("proTipTitle")}</h4>
                  <p className="text-muted-foreground text-sm leading-relaxed">{t("proTipDesc")}</p>
                </div>
              </div>
            </div>

            <div className="bg-muted flex items-start gap-2 rounded-md p-3">
              <Info className="text-muted-foreground mt-0.5 h-4 w-4 flex-shrink-0" />
              <p className="text-muted-foreground text-xs">{t("notice")}</p>
            </div>
          </div>
        </AlertDialogBody>
        <AlertDialogFooter>
          <AlertDialogAction onClick={() => onOpenChange(false)}>{t("action")}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
